import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, MapPin, Calendar, Euro, ArrowRight, Plane } from 'lucide-react';
import { packagesByContinent, usaRoadTrips, australiaTrips, newZealandTrips } from '../packagesData';

// Real images for Asia
const realImages = {
  thailand: 'https://images.unsplash.com/photo-1540689200701-cc5cea6a5512?w=800&q=80',
  'sri-lanka': 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
  filipijnen: 'https://images.unsplash.com/photo-1694442948802-00beebcf6448?w=800&q=80',
  japan: 'https://images.unsplash.com/photo-1565618754154-c8011e5df2a6?w=800&q=80',
  maleisie: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800&q=80',
  cambodja: 'https://images.unsplash.com/photo-1768882902762-b0c17224c875?w=800&q=80',
  indonesie: 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?w=800&q=80',
  bali: 'https://images.unsplash.com/photo-1555400038-a088c772c8cd?w=800&q=80'
};

// Gradient placeholders for other continents
const gradients = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
];

const PackageCard = ({ reis, index, useRealImage = false }) => {
  const [showMore, setShowMore] = useState(false);

  const getImageStyle = () => {
    if (reis.image_url) {
      return { backgroundImage: `url(${reis.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }
    if (useRealImage && realImages[reis.id]) {
      return { backgroundImage: `url(${realImages[reis.id]})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }
    return { background: gradients[index % gradients.length] };
  };
  const imageStyle = getImageStyle();

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
      {/* Image */}
      <div 
        className="h-64 relative"
        style={imageStyle}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        {reis.populair && (
          <div className="absolute top-4 right-4 bg-yellow-400 text-blue-950 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
            <span>⭐</span>
            <span>POPULAIR</span>
          </div>
        )}
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-2xl font-bold text-white mb-1">{reis.naam}</h3>
          <div className="flex items-center gap-4 text-white/90 text-sm">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{reis.dagen} dagen</span>
            </div>
            <div className="flex items-center gap-1">
              <Euro className="w-4 h-4" />
              <span>Vanaf €{reis.prijs}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h4 className="text-sm font-semibold text-blue-900 mb-3">✨ Hoogtepunten:</h4>
        <div className="space-y-2 mb-4">
          {reis.hoogtepunten.map((punt) => (
            <div key={punt} className="flex items-start gap-2">
              <span className="text-sm text-gray-700">{punt}</span>
            </div>
          ))}
        </div>
        
        <div className="mb-4">
          <p className={`text-sm text-gray-600 ${!showMore && 'line-clamp-2'}`}>
            {reis.beschrijving}
          </p>
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-blue-700 text-sm font-medium mt-1 hover:text-blue-900 transition-colors"
          >
            {showMore ? '↑ Minder' : '↓ En nog meer'}
          </button>
        </div>

        {reis.note && (
          <p className="text-xs text-blue-700 font-medium mb-4">
            ℹ️ {reis.note}
          </p>
        )}

        <Link
          to="/contact"
          className="w-full px-6 py-3 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 hover:from-blue-800 hover:to-blue-600 transition-all"
        >
          Vraag Offerte Aan
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
};

const Pakketreizen = () => {
  const [activeContinent, setActiveContinent] = useState('azie');

  const continents = [
    { id: 'europa', naam: '🌍 Europa', color: 'indigo' },
    { id: 'azie', naam: '🌏 Azië', color: 'blue' },
    { id: 'afrika', naam: '🌍 Afrika', color: 'green' },
    { id: 'zuidAmerika', naam: '🌎 Zuid-Amerika', color: 'red' },
    { id: 'noordAmerika', naam: '🗽 Noord-Amerika', color: 'purple' },
    { id: 'oceanie', naam: '🏝️ Oceanië', color: 'teal' }
  ];

  const getColorClass = (color, type = 'bg') => {
    const colors = {
      indigo: type === 'bg' ? 'bg-indigo-800' : 'border-indigo-800',
      blue: type === 'bg' ? 'bg-blue-900' : 'border-blue-900',
      green: type === 'bg' ? 'bg-green-700' : 'border-green-700',
      red: type === 'bg' ? 'bg-red-700' : 'border-red-700',
      purple: type === 'bg' ? 'bg-purple-700' : 'border-purple-700',
      teal: type === 'bg' ? 'bg-teal-700' : 'border-teal-700'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 bg-gradient-to-r from-blue-900 via-blue-800 to-green-700">
        <div className="max-w-7xl mx-auto text-center">
          <Plane className="w-16 h-16 text-white mx-auto mb-6 animate-bounce" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Onze Maatwerkreizen
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Ontdek de wereld met onze zorgvuldig samengestelde rondreizen. 
            Van tropische stranden tot indrukwekkende bergtoppen - jouw droomreis wacht!
          </p>
        </div>
      </section>

      {/* Continent Selector */}
      <section className="py-8 px-6 bg-white sticky top-0 z-40 shadow-md">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {continents.map((continent) => (
              <button
                key={continent.id}
                onClick={() => setActiveContinent(continent.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeContinent === continent.id
                    ? `${getColorClass(continent.color)} text-white shadow-lg scale-105`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {continent.naam}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Europa */}
          {activeContinent === 'europa' && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-8 h-8 text-indigo-800" />
                <h2 className="text-4xl font-bold text-indigo-800">Europa Reizen</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {packagesByContinent.europa.map((reis, index) => (
                  <PackageCard key={reis.id} reis={reis} index={index} />
                ))}
              </div>
            </div>
          )}

          {/* Azië */}
          {activeContinent === 'azie' && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-8 h-8 text-blue-900" />
                <h2 className="text-4xl font-bold text-blue-900">Azië Reizen</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {packagesByContinent.azie.map((reis, index) => (
                  <PackageCard key={reis.id} reis={reis} index={index} useRealImage={true} />
                ))}
              </div>
            </div>
          )}

          {/* Afrika */}
          {activeContinent === 'afrika' && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-8 h-8 text-green-700" />
                <h2 className="text-4xl font-bold text-green-700">Afrika Reizen</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {packagesByContinent.afrika.map((reis, index) => (
                  <PackageCard key={reis.id} reis={reis} index={index} />
                ))}
              </div>
            </div>
          )}

          {/* Zuid-Amerika */}
          {activeContinent === 'zuidAmerika' && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-8 h-8 text-red-700" />
                <h2 className="text-4xl font-bold text-red-700">Zuid-Amerika Reizen</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {packagesByContinent.zuidAmerika.map((reis, index) => (
                  <PackageCard key={reis.id} reis={reis} index={index} />
                ))}
              </div>
            </div>
          )}

          {/* Noord-Amerika */}
          {activeContinent === 'noordAmerika' && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-8 h-8 text-purple-700" />
                <h2 className="text-4xl font-bold text-purple-700">Noord-Amerika Reizen</h2>
              </div>
              
              {/* Reguliere reizen */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {packagesByContinent.noordAmerika.map((reis, index) => (
                  <PackageCard key={reis.id} reis={reis} index={index} />
                ))}
              </div>

              {/* USA Autoreizen */}
              <div className="mt-16">
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-2xl p-8 mb-8">
                  <h3 className="text-3xl font-bold text-purple-900 mb-2">🚗 Amerika Autoreizen</h3>
                  <p className="text-purple-700">Ontdek het Amerikaanse Westen op eigen tempo met een huurauto</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {usaRoadTrips.map((reis, index) => (
                    <PackageCard key={reis.id} reis={reis} index={index + 7} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Oceanië */}
          {activeContinent === 'oceanie' && (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-8 h-8 text-teal-700" />
                <h2 className="text-4xl font-bold text-teal-700">Oceanië Reizen</h2>
              </div>
              
              {/* Reguliere Oceanië reizen */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {packagesByContinent.oceanie.map((reis, index) => (
                  <PackageCard key={reis.id} reis={reis} index={index} />
                ))}
              </div>

              {/* Australië Specials */}
              <div className="mt-16">
                <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-2xl p-8 mb-8">
                  <h3 className="text-3xl font-bold text-orange-900 mb-2">🦘 Australië Reizen</h3>
                  <p className="text-orange-700">Ontdek de Oostkust van Australië</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                  {australiaTrips.map((reis, index) => (
                    <PackageCard key={reis.id} reis={reis} index={index + 6} />
                  ))}
                </div>
              </div>

              {/* Nieuw-Zeeland */}
              <div className="mt-16">
                <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-2xl p-8 mb-8">
                  <h3 className="text-3xl font-bold text-green-900 mb-2">🌿 Nieuw-Zeeland Reizen</h3>
                  <p className="text-green-700">Met auto of camper door het land van de Kiwi's</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {newZealandTrips.map((reis, index) => (
                    <PackageCard key={reis.id} reis={reis} index={index + 3} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-900 to-green-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Niet gevonden wat je zoekt?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Wij maken graag een reis op maat voor jou!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/rondreizen-tool"
              className="px-8 py-4 bg-white text-blue-900 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-lg"
            >
              Plan Je Eigen Rondreis
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pakketreizen;
